<!-- DefaultLayout.vue -->
<template>
  <div class="antialiased min-h-screen flex flex-col">
    <!-- Session Status Component -->
    <SessionStatus 
      :system="currentSystem" 
      :show-always="false"
    />
    
    <SidebarBar
      :is-sidebar-open="isSidebarOpen"
      @toggle-sidebar="toggleSidebar"
      @update:collapsed="updateCollapsed"
      @navigate="handleSidebarNavigation"
    />
    <main
      class="flex-1 transition-all duration-300"
      :class="{
        'md:ml-64': !isSidebarCollapsed && !isSidebarOpen,
        'md:ml-16': isSidebarCollapsed && !isSidebarOpen,
        'ml-0': !isSidebarOpen || !isMediumScreen,
      }"
    >
      <router-view v-slot="{ Component }">
        <component :is="Component" @login-required="showLoginPopup" />
      </router-view>
      <!-- Login Popup -->
      <div
        v-if="showPopup"
        class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      >
        <div class="bg-white rounded-md shadow-lg w-full max-w-sm mx-auto">
          <!-- Header -->
          <div class="p-6 border-b border-gray-100">
            <div class="flex items-center justify-between">
              <h2 class="text-lg font-semibold text-gray-900">Sign In</h2>
              <button
                @click="closePopup"
                class="text-gray-400 hover:text-gray-600"
              >
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Content -->
          <div class="p-6">
            <!-- Error Message -->
            <div
              v-if="loginError"
              class="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm"
            >
              {{ loginError }}
            </div>

            <!-- Step 1: System Selection -->
            <div v-if="currentStep === 1" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-3"
                  >Select System</label
                >
                <div class="space-y-2">
                  <label
                    class="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50"
                    :class="
                      system === 'eLearning'
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-gray-200'
                    "
                  >
                    <input
                      type="radio"
                      v-model="system"
                      value="eLearning"
                      class="sr-only"
                      @change="validateSystem"
                    />
                    <div
                      class="w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center"
                      :class="
                        system === 'eLearning'
                          ? 'border-orange-400 bg-orange-400'
                          : 'border-gray-300'
                      "
                    >
                      <div
                        v-if="system === 'eLearning'"
                        class="w-1.5 h-1.5 bg-white rounded-full"
                      ></div>
                    </div>
                    <span class="text-gray-900">E-Learning</span>
                  </label>

                  <label
                    class="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50"
                    :class="
                      system === 'hmis'
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-gray-200'
                    "
                  >
                    <input
                      type="radio"
                      v-model="system"
                      value="hmis"
                      class="sr-only"
                      @change="validateSystem"
                    />
                    <div
                      class="w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center"
                      :class="
                        system === 'hmis'
                          ? 'border-orange-400 bg-orange-400'
                          : 'border-gray-300'
                      "
                    >
                      <div
                        v-if="system === 'hmis'"
                        class="w-1.5 h-1.5 bg-white rounded-full"
                      ></div>
                    </div>
                    <span class="text-gray-900">Doctor Bookings</span>
                  </label>

                  <label
                    class="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50"
                    :class="
                      system === 'rafiki360'
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-gray-200'
                    "
                  >
                    <input
                      type="radio"
                      v-model="system"
                      value="rafiki360"
                      class="sr-only"
                      @change="validateSystem"
                    />
                    <div
                      class="w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center"
                      :class="
                        system === 'rafiki360'
                          ? 'border-orange-400 bg-orange-400'
                          : 'border-gray-300'
                      "
                    >
                      <div
                        v-if="system === 'rafiki360'"
                        class="w-1.5 h-1.5 bg-white rounded-full"
                      ></div>
                    </div>
                    <span class="text-gray-900">Rafiki360</span>
                  </label>

                  <label
                    class="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50"
                    :class="
                      system === 'appeals'
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-gray-200'
                    "
                  >
                    <input
                      type="radio"
                      v-model="system"
                      value="appeals"
                      class="sr-only"
                      @change="validateSystem"
                    />
                    <div
                      class="w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center"
                      :class="
                        system === 'appeals'
                          ? 'border-orange-400 bg-orange-400'
                          : 'border-gray-300'
                      "
                    >
                      <div
                        v-if="system === 'appeals'"
                        class="w-1.5 h-1.5 bg-white rounded-full"
                      ></div>
                    </div>
                    <span class="text-gray-900">Appeals & Donations</span>
                  </label>
                </div>
              </div>

              <button
                @click="currentStep = 2"
                :disabled="!['eLearning', 'hmis', 'rafiki360', 'appeals'].includes(system)"
                class="w-full py-2 px-4 bg-orange-400 text-white rounded hover:bg-orange-500 transition-colors"
                :class="{
                  'opacity-50 cursor-not-allowed': ![
                    'eLearning',
                    'hmis',
                    'rafiki360',
                    'appeals',
                  ].includes(system),
                }"
              >
                Next
              </button>
            </div>

            <!-- Step 2: Login Form -->
            <div v-else class="space-y-4">
              <button
                @click="currentStep = 1"
                class="text-sm text-gray-500 hover:text-gray-700 mb-2"
              >
                ← Back
              </button>

              <div>
                <input
                  v-model="email"
                  type="text"
                  placeholder="Email or phone number"
                  class="w-full p-3 border border-gray-200 rounded focus:border-orange-400 focus:outline-none"
                />
              </div>

              <div class="relative">
                <input
                  v-model="password"
                  :type="passwordVisible ? 'text' : 'password'"
                  placeholder="Password"
                  class="w-full p-3 border border-gray-200 rounded focus:border-orange-400 focus:outline-none pr-10"
                />
                <button
                  @click="togglePasswordVisibility"
                  class="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  👁️
                </button>
              </div>

              <div class="flex justify-between space-x-2 pt-2">
                <button
                  @click="handleLogin"
                  class="py-2 px-4 bg-orange-400 text-white rounded hover:bg-orange-500 transition-colors"
                >
                  Sign In
                </button>
                <button
                  @click="closePopup"
                  class="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import SidebarBar from "./SideBar.vue";
import SessionStatus from "@/components/SessionStatus.vue";
import { useStore } from "vuex";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { AuthAPI } from "@/services/auth";
import { setApiSystem } from "@/services/api";

export default {
  name: "DefaultLayout",
  components: {
    SidebarBar,
    SessionStatus,
  },
  data() {
    return {
      isSidebarOpen: false,
      isSidebarCollapsed: false,
      isMediumScreen: false,
      showPopup: false,
      email: "",
      password: "",
      passwordVisible: false,
      currentStep: 1,
      system: "eLearning",
      loginError: "",
      intendedRoute: null,
    };
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const currentSystem = computed(() => route.meta.system || "eLearning");

    return { store, route, router, toast, currentSystem };
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
    this.store.dispatch("initializeAuth");
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkScreenSize);
  },
  methods: {
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    updateCollapsed(value) {
      this.isSidebarCollapsed = value;
    },
    checkScreenSize() {
      this.isMediumScreen = window.innerWidth >= 768;
    },
    validateSystem() {
      if (!["eLearning", "hmis", "rafiki360", "appeals"].includes(this.system)) {
        this.system = "eLearning";
      }
    },
    showLoginPopup() {
      const requiredSystem =
        this.route.meta.system || "eLearning" || "hmis" || "rafiki360" ||"appeals";
      if (!this.store.getters.isAuthenticated(requiredSystem)) {
        this.system = requiredSystem;
        this.currentStep = 1;
        this.intendedRoute = this.route.fullPath;
        console.log("Setting intended route:", this.intendedRoute);
        this.store.commit("setIntendedRoute", this.intendedRoute);
        this.showPopup = true;
      }
    },
    handleSidebarNavigation(path) {
      const systemMap = {
        "/e-learning": "eLearning",
        "/doctor-bookings": "hmis",
        "/rafiki360": "rafiki360",
        "/appeals-donations": "appeals",
      };
      const requiredSystem = systemMap[path] || "eLearning";

      // Set the current system and update API configuration
      this.system = requiredSystem;
      setApiSystem(requiredSystem);
      console.log(`Set current system to: ${requiredSystem}`);

      if (!this.store.getters.isAuthenticated(requiredSystem)) {
        this.currentStep = 1;
        this.intendedRoute = path;
        console.log("Setting intended route from sidebar:", path);
        this.store.commit("setIntendedRoute", path);
        this.showPopup = true;
      } else {
        this.router.push(path);
      }
    },
    closePopup() {
      this.showPopup = false;
      this.email = "";
      this.password = "";
      this.loginError = "";
      this.currentStep = 1;
      this.intendedRoute = null;
      this.system = this.route.meta.system || "eLearning";
    },
    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible;
    },
    async handleLogin() {
      this.loginError = "";

      if (!["eLearning", "hmis", "rafiki360", "appeals"].includes(this.system)) {
        this.loginError = "Invalid system selected.";
        this.currentStep = 1;
        return;
      }

      if (!this.email || !this.password) {
        this.loginError = "Please fill all fields.";
        return;
      }

      try {
        console.log("Setting API system to:", this.system);
        setApiSystem(this.system);
        console.log(
          "Attempting login with system:",
          this.system,
          "email:",
          this.email
        );

        const response = await AuthAPI.login(
          { email: this.email, password: this.password },
          this.system
        );

        console.log("Login response:", response);

        const { user, token } = response;
        if (!user || !token) {
          throw new Error("Invalid response: Missing user or token");
        }

        await this.store.dispatch("login", {
          data: user,
          token,
          system: this.system,
        });
        console.log("Store state after login:", this.store.state);
        console.log(
          "Is authenticated for",
          this.system,
          ":",
          this.store.getters.isAuthenticated(this.system)
        );

        // Show success toast message
        const systemDisplayNames = {
          eLearning: 'E-Learning',
          hmis: 'Doctor Bookings',
          rafiki360: 'Rafiki360',
          appeals: 'Appeals & Donations'
        };
        const displayName = systemDisplayNames[this.system] || this.system;
        this.toast(
          `Successfully logged into ${displayName}! Welcome back, ${user.first_name || user.name || user.email || 'User'}!`,
          {
            type: 'success',
            timeout: 4000,
            closeOnClick: true,
            pauseOnFocusLoss: true,
            pauseOnHover: true,
            draggable: true,
              draggablePercent: 0.6,
              showCloseButtonOnHover: false,
              hideProgressBar: false,
              closeButton: "button",
              icon: true,
              rtl: false
          }
        );

        let redirectPath = this.system === "hmis"
          ? "/doctor-bookings"
          : this.system === "rafiki360"
          ? "/rafiki360"
          : this.system === "appeals"
          ? "/appeals-donations"
          : "/e-learning";
        const intended = this.store.getters.intendedRoute;
        if (intended && intended.startsWith(redirectPath)) {
          redirectPath = intended;
        }

        console.log("Redirecting to:", redirectPath);
        console.log("Intended route:", this.store.getters.intendedRoute);
        console.log("Redirecting to:", redirectPath);

        this.closePopup();
        await this.router.push(redirectPath);
        this.store.commit("clearIntendedRoute");
      } catch (error) {
        console.error("Login error:", error.response?.data || error.message);
        this.loginError =
          error.response?.data?.message ||
          error.message ||
          "Wrong email or password";
      }
    },
  },
};
</script>
